// Power-ups: Lynreserve, Nødaflastning og Ring til Norge. Knapper med nedkøling, kun på baner der
// har dem. Nødaflastning spørger først, hvilken by der skal slukke et øjeblik.
// Knappen gløder, mens effekten kører, og nikker, når den er klar igen.

import type { Game } from '../game';
import type { Mechanic, PowerId } from '../sim';
import type { P } from '../render/geo';
import { check } from '../sim/actions';
import { HZ_MAX, hzText, hzZone } from '../sim/hz';
import { powerKind, powerWaitS, POWER_IDS, sheddable } from '../sim/powers';
import { DRIFT } from '../sim/roles';
import { h, kbd, setAttr, setText, toggle } from './dom';
import { ICON } from './icons';

export interface PowerHandlers {
  use(id: PowerId, target?: string): void;
  /** Spor: tillid. Driftsvagtens frekvensnål: regulér tilbage mod 50,00 Hz. */
  trim(): void;
}

/** Frekvensnålens tast, tekst og forklaring (sim/hz.ts). */
export const HZ_KEY = 'R';
export const HZ_TEXT = {
  label: 'Frekvens',
  what: 'Hold nålen i det grønne, 49,9-50,1 Hz. Et tryk regulerer den hjem',
} as const;

/** Lille viser: grøn midte, gul og orange ude i siderne. Nålen drejes med --hz-a (CSS). */
const GAUGE_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path class="hz-arc o" d="M3.5 17A9 9 0 0 1 5.6 9.4"/><path class="hz-arc y" d="M5.6 9.4A9 9 0 0 1 9 6.6"/><path class="hz-arc g" d="M9 6.6A9 9 0 0 1 15 6.6"/><path class="hz-arc y" d="M15 6.6A9 9 0 0 1 18.4 9.4"/><path class="hz-arc o" d="M18.4 9.4A9 9 0 0 1 20.5 17"/><line class="hz-needle" x1="12" y1="17" x2="12" y2="8"/><circle class="hz-pivot" cx="12" cy="17" r="2"/></svg>';

/** Genvejstasterne: venstre hånd, i samme rækkefølge som knapperne. */
export const POWER_KEY: Record<PowerId, string> = { fcr: 'Q', shed: 'W', norway: 'E' };

/** `short` er knappens ord på en telefon (Spor: telefonkort: "Nødafl..." var klippet ved 390 px). */
export const POWER_TEXT: Record<PowerId, { label: string; short: string; what: string }> = {
  fcr: { label: 'Lynreserve', short: 'Lyn', what: 'Hurtig strøm i 10 s, og mørke byer får 5 s ekstra' },
  shed: { label: 'Nødaflastning', short: 'Aflast', what: 'Sluk én by i 10 s. Den bliver sur, men går ikke i sort' },
  norway: { label: 'Ring til Norge', short: 'Norge', what: 'Norsk vandkraft hjælper via Tjele i 15 s' },
};

interface PowerBtn {
  el: HTMLButtonElement;
  state: HTMLElement;
}

export class PowerBar {
  readonly el: HTMLElement;
  private btns: Record<PowerId, PowerBtn>;
  private pickEl = h('div', { class: 'shed-pick', hidden: true, role: 'menu', 'aria-label': 'Vælg en by til nødaflastning' });
  private picking = false;
  private pickKey = '';

  constructor(private handlers: PowerHandlers) {
    const mk = (id: PowerId): PowerBtn => {
      const state = h('span', { class: 'power-state' });
      const el = h(
        'button',
        { type: 'button', class: 'power', 'data-power': id, title: POWER_TEXT[id].what, onclick: () => this.press(id) },
        h('span', { class: 'power-icon', html: id === 'shed' ? ICON.moon : id === 'norway' ? ICON.fjeld : ICON.bolt, 'aria-hidden': 'true' }),
        // Det fulde navn læses altid højt; det korte ord vises kun, hvor der er trangt (CSS).
        h('span', { class: 'power-label' }, h('span', { class: 'pl-long' }, POWER_TEXT[id].label), h('span', { class: 'pl-short', 'aria-hidden': 'true' }, POWER_TEXT[id].short)),
        state,
      );
      kbd(el, POWER_KEY[id], POWER_KEY[id].toLowerCase(), POWER_TEXT[id].what);
      return { el, state };
    };
    this.btns = { fcr: mk('fcr'), shed: mk('shed'), norway: mk('norway') };
    // Telefonens række er trang: der står "Hz" over tallet, og enheden bag tallet skjules (CSS).
    this.hzNum = h('span', {});
    this.hzState = h('span', { class: 'power-state' }, this.hzNum, h('span', { class: 'hz-unit' }, ' Hz'));
    this.hzBtn = h(
      'button',
      { type: 'button', class: 'power hz', hidden: true, title: HZ_TEXT.what, onclick: () => this.pressHz() },
      h('span', { class: 'power-icon hz-gauge', html: GAUGE_SVG, 'aria-hidden': 'true' }),
      h('span', { class: 'power-label' }, h('span', { class: 'pl-long' }, HZ_TEXT.label), h('span', { class: 'pl-short', 'aria-hidden': 'true' }, 'Hz')),
      this.hzState,
    ) as HTMLButtonElement;
    kbd(this.hzBtn, HZ_KEY, HZ_KEY.toLowerCase(), HZ_TEXT.what);
    this.el = h('div', { class: 'powers', hidden: true }, this.pickEl, this.hzBtn, ...POWER_IDS.map((id) => this.btns[id].el));
    // Et tryk ved siden af eller Escape lukker bylisten igen.
    document.addEventListener('pointerdown', (e) => {
      if (this.picking && !this.el.contains(e.target as Node)) this.picking = false;
    });
    window.addEventListener('keydown', (e) => {
      if (this.picking && e.key === 'Escape') this.picking = false;
    });
  }

  private game: Game | null = null;
  private hzBtn: HTMLButtonElement;
  private hzState: HTMLElement;
  private hzNum: HTMLElement;

  /** Tryk på frekvensnålen (også R). Grunden tilbage, når den ikke kan bruges, som press(). */
  pressHz(): string | null {
    const b = this.hzBtn;
    if (b.hidden || this.el.hidden) return b.hidden ? 'Frekvensen er Driftsvagtens job.' : null;
    if (b.disabled) {
      this.kick(b, 'nope');
      return 'Frekvensen kan ikke reguleres lige nu.';
    }
    const dev = this.game?.state.hz?.dev ?? 0;
    if (hzZone(dev) === 'green' && Math.abs(dev) < 30) {
      this.kick(b, 'nope');
      return 'Nålen står på 50,00 Hz. Intet at regulere.';
    }
    this.kick(b, 'kick');
    this.handlers.trim();
    return null;
  }

  /** Midten af frekvensknappen (til hånden), hvis den står der. */
  hzCenter(): P | null {
    const b = this.hzBtn;
    if (this.el.hidden || b.hidden || b.disabled) return null;
    const r = b.getBoundingClientRect();
    return r.width > 0 ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
  }

  /**
   * Tryk på knappen (også fra tastaturet). Kan den ikke bruges, ryster den og giver grunden tilbage,
   * så tastaturet kan sige den højt (en knap, man ikke kan se, svarer ikke).
   */
  press(id: PowerId): string | null {
    const b = this.btns[id].el;
    const label = POWER_TEXT[id].label;
    if (b.hidden) return `${label} kommer på en senere bane.`;
    if (this.el.hidden) return null;
    if (b.disabled) {
      this.kick(b, 'nope');
      const wait = this.game ? powerWaitS(this.game.state, id) : 0;
      return wait > 0 ? `${label} er klar om ${wait} s.` : `${label} kan ikke bruges lige nu.`;
    }
    this.kick(b, 'kick');
    if (id === 'shed') {
      this.picking = !this.picking;
      this.pickKey = '';
      return null;
    }
    this.picking = false;
    this.handlers.use(id);
    return null;
  }

  private kick(b: HTMLElement, cls: 'nope' | 'kick'): void {
    b.classList.remove('nope', 'kick');
    void b.offsetWidth;
    b.classList.add(cls);
  }

  /** Vælg by nummer i (0-baseret) i listen til Nødaflastning. Sand, hvis listen var åben. */
  pickIndex(i: number): boolean {
    if (!this.isPicking) return false;
    const b = this.pickEl.querySelectorAll<HTMLButtonElement>('.shed-city')[i];
    b?.click();
    return true;
  }

  /** Er bylisten til Nødaflastning åben? */
  get isPicking(): boolean {
    return this.picking && !this.pickEl.hidden;
  }

  /** Midten af knappen (til hånden), hvis den kan trykkes. */
  center(id: PowerId): P | null {
    const b = this.btns[id].el;
    if (this.el.hidden || b.hidden || b.disabled) return null;
    const r = b.getBoundingClientRect();
    return r.width > 0 ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
  }

  /** Midten af byens knap i listen til Nødaflastning. */
  pickCenter(city: string): P | null {
    if (!this.isPicking) return null;
    const b = this.pickEl.querySelector<HTMLElement>(`[data-city="${city}"]`);
    const r = b?.getBoundingClientRect();
    return r && r.width > 0 ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
  }

  private home: { parent: Node; next: Node | null } | null = null;

  /**
   * Nederst i skinnen i bredformat. På telefonen i højformat står knapperne som en række øverst i
   * værktøjslinjen (`dock`), så de ikke svæver over kortet og dækker byer, når nettet er vokset.
   */
  place(rail: boolean, dock: HTMLElement | null = null): void {
    toggle(this.el, 'rail', rail);
    const inbar = !rail && dock !== null;
    toggle(this.el, 'inbar', inbar);
    if (inbar && this.el.parentNode !== dock) {
      if (!this.home && this.el.parentNode) this.home = { parent: this.el.parentNode, next: this.el.nextSibling };
      dock.prepend(this.el);
    } else if (!inbar && this.home && this.el.parentNode !== this.home.parent) {
      this.home.parent.insertBefore(this.el, this.home.next);
    }
  }

  /** Står knapperne i værktøjslinjen? */
  get docked(): boolean {
    return this.el.classList.contains('inbar');
  }

  update(game: Game, mechanics: readonly Mechanic[], covered: boolean): void {
    this.game = game;
    const s = game.state;
    let any = false;
    for (const id of POWER_IDS) {
      const b = this.btns[id];
      // Makkerspillets roller: alle tre nødknapper er Driftsvagtens (spor coop).
      const shown = mechanics.includes(id) && !game.theirs(powerKind(id));
      b.el.hidden = !shown;
      if (!shown) continue;
      any = true;
      const p = s.power[id];
      const active = s.clockTick < p.until;
      const wait = powerWaitS(s, id);
      const ok = check(s, powerKind(id), id === 'shed' ? this.firstSheddable() : undefined, undefined, game.player).ok;
      b.el.disabled = !game.canBuildNow || (!ok && !(id === 'shed' && wait === 0));
      toggle(b.el, 'active', active);
      toggle(b.el, 'on', id === 'shed' && this.picking);
      toggle(b.el, 'ready', !active && wait === 0 && ok && !b.el.disabled);
      // IGN-løkke 2: "Klar" på en knap, der ikke kan trykkes, lød som en fejl. Siger nu hvorfor.
      const held = !game.canBuildNow && !game.over;
      const txt = active
        ? `Kører ${Math.ceil((p.until - s.clockTick) / 10)} s`
        : wait > 0
          ? `${wait} s`
          : held
            ? game.cardHold
              ? 'Kortet først'
              : 'Pause'
            : ok
              ? 'Klar'
              : 'Ikke nu';
      setText(b.state, txt);
      setAttr(b.el, 'aria-label', `${POWER_TEXT[id].label}. ${txt}. ${POWER_TEXT[id].what}.`);
    }
    if (this.updateHz(game)) any = true;
    this.el.hidden = covered || !any;
    if (this.picking && (covered || this.btns.shed.el.disabled)) this.picking = false;
    this.updatePick();
  }

  /**
   * Frekvensnålen står hos Driftsvagten på baner med roller (og hos den, der er alene tilbage i et
   * rum). Gul: nik og "regulér"; orange: reserven koster, knappen bliver orange. Aldrig rød.
   */
  private updateHz(game: Game): boolean {
    const s = game.state;
    const b = this.hzBtn;
    const mine = !!s.hz && (game.player === DRIFT || (game.remote !== null && !game.rolesLive));
    b.hidden = !mine;
    if (!mine || !s.hz) return false;
    const dev = s.hz.dev;
    const zone = hzZone(dev);
    const ok = check(s, 'trim_hz', undefined, undefined, game.player).ok;
    b.disabled = !game.canBuildNow;
    b.style.setProperty('--hz-a', `${Math.round((dev / HZ_MAX) * 75)}deg`);
    toggle(b, 'ready', zone === 'yellow' && ok && !b.disabled);
    toggle(b, 'warn', zone === 'orange');
    const txt = hzText(dev);
    setText(this.hzNum, txt.replace(' Hz', ''));
    const say = zone === 'green' ? 'I det grønne' : zone === 'yellow' ? 'Uden for båndet, regulér' : 'Langt ude, reserven koster';
    setAttr(b, 'aria-label', `${HZ_TEXT.label}. ${txt}. ${say}.`);
    return true;
  }

  private firstSheddable(): string | undefined {
    return this.game?.state.nodes.find(sheddable)?.id;
  }

  /** Listen over byer, der kan slukke et øjeblik: de største først. */
  private updatePick(): void {
    const g = this.game;
    if (!this.picking || !g) {
      this.pickEl.hidden = true;
      return;
    }
    const cities = g.state.nodes.filter(sheddable).sort((a, b) => (b.city?.demandMw ?? 0) - (a.city?.demandMw ?? 0)).slice(0, 6);
    // Byen i nød: den, hvis mørkeur er længst fremme. Den står i overskriften, så valget giver mening.
    let needy: (typeof cities)[number] | null = null;
    for (const n of g.state.nodes) {
      const c = n.city;
      if (!c || !n.active || n.phantom || c.darkTicks > 0 || c.clock <= 0) continue;
      if (!needy || c.clock > (needy.city?.clock ?? 0)) needy = n;
    }
    const key = `${needy?.id ?? ''}|${cities.map((n) => n.id).join(',')}`;
    this.pickEl.hidden = false;
    if (key === this.pickKey) return;
    this.pickKey = key;
    this.pickEl.replaceChildren(
      h('p', { class: 'shed-title' }, needy ? `${needy.name} mangler strøm. Hvem må holde 10 s pause?` : 'Hvilken by må holde 10 s pause?'),
      ...cities.map((n, i) =>
        h(
          'button',
          {
            type: 'button',
            class: n === needy ? 'shed-city needy' : 'shed-city',
            role: 'menuitem',
            'data-city': n.id,
            onclick: () => {
              this.picking = false;
              this.handlers.use('shed', n.id);
            },
          },
          h('span', {}, n.name, h('small', {}, n === needy ? ' i nød' : ` ${Math.round(n.city?.demandMw ?? 0)} MW`)),
          i < 9 ? h('kbd', { class: 'kbd', 'aria-hidden': 'true' }, String(i + 1)) : null,
        ),
      ),
    );
  }
}
